import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Copy, Warning, CheckCircle, ArrowRight, Trash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DuplicateDetector = ({ open, onClose, projectId, onDeleteScene }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDetect = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/duplicates`);
      setResult(res.data);
      if (res.data.duplicate_pairs_found === 0) {
        toast.success('No duplicate scenes found!');
      } else {
        toast.info(`Found ${res.data.duplicate_pairs_found} potential duplicate(s)`);
      }
    } catch (err) {
      toast.error('Failed to detect duplicates');
    } finally {
      setLoading(false);
    }
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 80) return '#EF4444'; // Red - very similar
    if (similarity >= 70) return '#F59E0B'; // Yellow - somewhat similar
    return '#10B981'; // Green - less similar
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Copy size={24} weight="fill" className="text-[#FF5D01]" />
            Duplicate Scene Detector
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {!result ? (
            <div className="text-center py-8">
              <Copy size={48} className="mx-auto mb-4 text-[#A1A1AA]" />
              <p className="text-[#A1A1AA] mb-4">
                Scan your scenes to find potential duplicates or very similar visual prompts.
              </p>
              <Button
                onClick={handleDetect}
                disabled={loading}
                className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                data-testid="detect-duplicates-btn"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Copy size={18} weight="fill" />
                )}
                {loading ? 'Scanning...' : 'Scan for Duplicates'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg">
                <div className="flex items-center gap-2">
                  {result.duplicate_pairs_found === 0 ? (
                    <CheckCircle size={20} weight="fill" className="text-[#10B981]" />
                  ) : (
                    <Warning size={20} weight="fill" className="text-[#F59E0B]" />
                  )}
                  <span className="text-sm">{result.message}</span>
                </div>
                <span className="text-xs text-[#A1A1AA]">{result.total_scenes} scenes total</span>
              </div>

              {/* Duplicates List */}
              {result.duplicates && result.duplicates.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {result.duplicates.map((dup, index) => (
                      <div
                        key={index}
                        className="p-4 bg-[#0a0a0a] border border-[#333333] rounded-lg"
                      >
                        {/* Similarity badge */}
                        <div className="flex items-center justify-between mb-3">
                          <div
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{
                              backgroundColor: `${getSimilarityColor(dup.similarity)}20`,
                              color: getSimilarityColor(dup.similarity)
                            }}
                          >
                            {dup.similarity}% similar
                          </div>
                          <span className="text-xs text-[#A1A1AA]">{dup.recommendation}</span>
                        </div>

                        {/* Scene comparison */}
                        <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-start">
                          {/* Scene 1 */}
                          <div className="p-3 bg-[#171717] rounded border border-[#333333]">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-[#FF5D01]">
                                Scene #{dup.scene1.scene_number}
                              </span>
                              {onDeleteScene && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    onDeleteScene(dup.scene1.id);
                                    toast.success(`Deleted scene #${dup.scene1.scene_number}`);
                                  }}
                                  className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-[#EF4444]"
                                >
                                  <Trash size={14} />
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-[#A1A1AA] line-clamp-2">{dup.scene1.summary}</p>
                            <p className="text-xs text-[#666] mt-1 line-clamp-2 italic">
                              "{dup.scene1.visual_prompt}..."
                            </p>
                          </div>

                          {/* Arrow */}
                          <div className="flex items-center justify-center h-full">
                            <ArrowRight size={20} className="text-[#A1A1AA]" />
                          </div>

                          {/* Scene 2 */}
                          <div className="p-3 bg-[#171717] rounded border border-[#333333]">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-[#FF5D01]">
                                Scene #{dup.scene2.scene_number}
                              </span>
                              {onDeleteScene && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    onDeleteScene(dup.scene2.id);
                                    toast.success(`Deleted scene #${dup.scene2.scene_number}`);
                                  }}
                                  className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-[#EF4444]"
                                >
                                  <Trash size={14} />
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-[#A1A1AA] line-clamp-2">{dup.scene2.summary}</p>
                            <p className="text-xs text-[#666] mt-1 line-clamp-2 italic">
                              "{dup.scene2.visual_prompt}..."
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto mb-4 text-[#10B981]" />
                  <p className="text-[#A1A1AA]">All your scenes are unique!</p>
                </div>
              )}

              {/* Rescan button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleDetect}
                  disabled={loading}
                  className="border-[#333333] text-white hover:bg-[#262626] gap-2"
                >
                  {loading ? 'Scanning...' : 'Rescan'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
